import { t } from '@/i18n'

/**
 * 合约管理相关枚举定义
 * 使用动态国际化方式定义
 */

/** 约价性质枚举 */
export const PRICING_NATURE_ENUM = {
  SELF_PRICING: {
    value: 1,
    get label() {
      return t('saas.enums.pricingNature.selfPricing')
    }
  },
  CUSTOMER_PRICING: {
    value: 2,
    get label() {
      return t('saas.enums.pricingNature.customerPricing')
    }
  },
  OVERSEAS_AGENT_PRICING: {
    value: 3,
    get label() {
      return t('saas.enums.pricingNature.overseasAgentPricing')
    }
  },
  NO_PRICING: {
    value: 4,
    get label() {
      return t('saas.enums.pricingNature.noPricing')
    }
  },
  PEER_PRICING: {
    value: 5,
    get label() {
      return t('saas.enums.pricingNature.peerPricing')
    }
  },
  BCO_CONTRACT: {
    value: 8,
    get label() {
      return t('saas.enums.pricingNature.bcoContract')
    }
  },
  AFC_PRICING: {
    value: 6,
    get label() {
      return t('saas.enums.pricingNature.afcPricing')
    }
  },
  MIXED_PRICING: {
    value: 7,
    get label() {
      return t('saas.enums.pricingNature.mixedPricing')
    }
  }
} as const

/** 适用品名枚举 */
export const COMMODITY_ENUM = {
  FAK: {
    value: 1,
    get label() {
      return t('saas.enums.commodity.fak')
    }
  },
  DANGEROUS_GOODS: {
    value: 2,
    get label() {
      return t('saas.enums.commodity.dangerousGoods')
    }
  },
  SPECIAL_CONTAINER: {
    value: 3,
    get label() {
      return t('saas.enums.commodity.specialContainer')
    }
  },
  FROZEN_GOODS: {
    value: 4,
    get label() {
      return t('saas.enums.commodity.frozenGoods')
    }
  },
  CHEMICAL_GOODS: {
    value: 5,
    get label() {
      return t('saas.enums.commodity.chemicalGoods')
    }
  },
  TEXTILE_GOODS: {
    value: 6,
    get label() {
      return t('saas.enums.commodity.textileGoods')
    }
  },
  OTHER: {
    value: 7,
    get label() {
      return t('saas.enums.commodity.other')
    }
  }
} as const

/** 舱保枚举 */
export const SPACE_GUARANTEE_ENUM = {
  GUARANTEE_20: {
    value: 1,
    get label() {
      return t('saas.enums.spaceGuarantee.guarantee20')
    }
  },
  GUARANTEE_50: {
    value: 2,
    get label() {
      return t('saas.enums.spaceGuarantee.guarantee50')
    }
  },
  GUARANTEE_100: {
    value: 3,
    get label() {
      return t('saas.enums.spaceGuarantee.guarantee100')
    }
  },
  GUARANTEE_300: {
    value: 4,
    get label() {
      return t('saas.enums.spaceGuarantee.guarantee300')
    }
  },
  NO_GUARANTEE: {
    value: 5,
    get label() {
      return t('saas.enums.spaceGuarantee.noGuarantee')
    }
  }
} as const

/** 合约状态枚举 */
export const CONTRACT_STATUS_ENUM = {
  ENABLED: {
    value: 1,
    get label() {
      return t('saas.enums.contractStatus.enabled')
    }
  },
  DISABLED: {
    value: 0,
    get label() {
      return t('saas.enums.contractStatus.disabled')
    }
  }
} as const

/**
 * 根据枚举值获取对应的标签
 */
export const getEnumLabel = (enumObj: any, value: any, defaultValue: any = ''): string => {
  const entry = Object.values(enumObj).find((item: any) =>
    item && typeof item === 'object' && 'value' in item && item.value === value
  ) as any
  return entry?.label || defaultValue
}

/**
 * 空值（null/undefined/''）统一返回空串，避免 String(null) 兜底展示成 'null'
 */
const safeEnumLabel = (enumObj: any, value: any): string => {
  if (value === null || value === undefined || value === '') return ''
  return getEnumLabel(enumObj, value, String(value))
}

/**
 * 将枚举对象转换为下拉选项数组
 */
export const toOptions = (enumObj: Record<string, { value: number; label: string }>) =>
  Object.values(enumObj).map(item => ({ value: item.value, label: item.label }))

// 便于业务直接导入使用的选项集合
export const PRICING_NATURE_OPTIONS = () => toOptions(PRICING_NATURE_ENUM)
export const COMMODITY_OPTIONS = () => toOptions(COMMODITY_ENUM)
export const SPACE_GUARANTEE_OPTIONS = () => toOptions(SPACE_GUARANTEE_ENUM)

/**
 * 约价性质标签获取函数
 */
export const getPricingNatureLabel = (value: number): string => {
  return safeEnumLabel(PRICING_NATURE_ENUM, value)
}

/**
 * 适用品名标签获取函数
 */
export const getCommodityLabel = (value: number): string => {
  return safeEnumLabel(COMMODITY_ENUM, value)
}

/**
 * 舱保标签获取函数
 */
export const getSpaceGuaranteeLabel = (value: number): string => {
  return safeEnumLabel(SPACE_GUARANTEE_ENUM, value)
}

/**
 * 合约状态标签获取函数
 */
export const getContractStatusLabel = (value: number): string => {
  return safeEnumLabel(CONTRACT_STATUS_ENUM, value)
}
